/**
 * Lead delivery.
 *
 * PRIMARY PATH (no JavaScript involved): every form posts natively to the
 * `lead-intake` edge function — see `leadFormAction()`. The server records the
 * lead, emails the leads inbox, pushes it to Housecall Pro, then redirects the
 * visitor to /thank-you. Nothing in that chain can be blocked by a privacy
 * extension, an in-app browser, or a dropped background request.
 *
 * BACKUP PATH: this module also keeps a durable copy of every submission in the
 * browser and re-sends it to `hcp-lead` on the next page load if the native POST
 * never reached the server. The server dedupes on submission_id, so a lead can
 * be delivered twice at most once — never duplicated, never lost.
 */

import { markLeadConfirmed, markLeadAwaiting } from "./googleAdsConversions";
import { leadFormElapsed } from "./formLoad";

function functionUrl(name: string): string | null {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (!url) return null;
  const base = `${url.replace(/\/$/, "")}/functions/v1/${name}`;
  return key ? `${base}?apikey=${encodeURIComponent(key)}` : base;
}

function endpoint(): string | null {
  return functionUrl("hcp-lead");
}

/**
 * The `action` every lead form posts to. Falls back to Web3Forms only if the
 * backend URL is somehow unavailable at build time, so a form is never dead.
 */
export function leadFormAction(): string {
  return functionUrl("lead-intake") ?? "https://api.web3forms.com/submit";
}

function newSubmissionId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `sub_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

/** Stable submission id per form element so retries/double clicks dedupe. */
const submissionIds = new WeakMap<HTMLFormElement, string>();

function submissionIdFor(form: HTMLFormElement): string {
  let id = submissionIds.get(form);
  if (!id) {
    id = newSubmissionId();
    submissionIds.set(form, id);
  }
  stampSubmissionId(form, id);
  return id;
}

/**
 * CRITICAL: writes the submission id into the form itself so the native POST to
 * `lead-intake` carries the SAME id as the background copy. Without this the two
 * paths look like two different leads and the customer is created twice.
 * The browser serialises form data after submit listeners run, so mutating the
 * DOM here is reflected in the native POST.
 */
function stampSubmissionId(form: HTMLFormElement, id: string): void {
  try {
    const existing = form.querySelector<HTMLInputElement>('input[name="submission_id"]');
    if (existing) {
      existing.value = id;
      return;
    }
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "submission_id";
    input.value = id;
    form.appendChild(input);
  } catch {
    /* never block the customer's submission */
  }
}

function str(value: FormDataEntryValue | null): string {
  return typeof value === "string" ? value.trim() : "";
}

/* ------------------------------------------------------------------ *
 * Durable pending-lead queue
 *
 * The beacon fired during the native form POST can be dropped (privacy
 * extensions, in-app browsers, unload races). Every lead is therefore also
 * written to localStorage and re-sent on the next page load (/thank-you is
 * always the very next page). The edge function dedupes by submission_id,
 * so a lead can never be created twice — but it also can never be lost.
 * ------------------------------------------------------------------ */

const QUEUE_KEY = "tron_pending_hcp_leads";
const MAX_QUEUE = 25;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type QueuedLead = { id: string; ts: number; payload: Record<string, string> };

function readQueue(): QueuedLead[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const list = raw ? (JSON.parse(raw) as QueuedLead[]) : [];
    if (!Array.isArray(list)) return [];
    const now = Date.now();
    return list.filter((e) => e && e.id && e.payload && now - (e.ts || 0) < MAX_AGE_MS);
  } catch {
    return [];
  }
}

function writeQueue(list: QueuedLead[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(list.slice(-MAX_QUEUE)));
  } catch {
    /* storage unavailable — beacon remains the only path */
  }
}

function enqueue(id: string, payload: Record<string, string>): void {
  const list = readQueue().filter((e) => e.id !== id);
  list.push({ id, ts: Date.now(), payload });
  writeQueue(list);
}

function dequeue(id: string): void {
  writeQueue(readQueue().filter((e) => e.id !== id));
}

/**
 * Re-sends any lead that was not confirmed as delivered. Safe to call on every
 * page load: the server dedupes on submission_id.
 */
export async function flushPendingLeads(): Promise<void> {
  const url = endpoint();
  if (!url) return;

  for (const entry of readQueue()) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry.payload),
        mode: "cors",
      });
      // Any HTTP answer means the server received and handled it (success,
      // duplicate, or a recorded/alerted failure). Only keep it queued when
      // the request itself never got through.
      
      // Only a success means the enquiry is genuinely captured, so only then
      // does it count as a lead for Google Ads.
      if (res.ok) {
        const result = await res.json();
        if (result.ok) dequeue(entry.id);
        if (result.ok) markLeadConfirmed(entry.id);
      }
    } catch {
      /* offline / blocked — try again on the next page load */
    }
  }
}

function buildPayload(form: HTMLFormElement): Record<string, string> | null {
  const fd = new FormData(form);

  const phone = str(fd.get("phone"));
  const name = str(fd.get("name"));
  if (!phone || !name) return null;

  return {
    submission_id: submissionIdFor(form),
    name,
    phone,
    email: str(fd.get("email")),
    message: str(fd.get("message")) || str(fd.get("details")),
    service: str(fd.get("service")) || str(fd.get("subject")),
    address: str(fd.get("address")),
    city: str(fd.get("city")),
    page_url: str(fd.get("page_url")) || (typeof window !== "undefined" ? window.location.href : ""),
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    page_name: str(fd.get("page_name")) || (typeof document !== "undefined" ? document.title : ""),
    gclid: str(fd.get("gclid")),
    click_id: str(fd.get("click_id")),
    click_id_type: str(fd.get("click_id_type")),
    click_at: str(fd.get("click_at")),
    consent_ad_user_data: str(fd.get("consent_ad_user_data")),
    utm_source: str(fd.get("utm_source")),
    utm_medium: str(fd.get("utm_medium")),
    utm_campaign: str(fd.get("utm_campaign")),
    utm_term: str(fd.get("utm_term")),
    utm_content: str(fd.get("utm_content")),
    referrer: str(fd.get("referrer")),
    ga_client_id: str(fd.get("ga_client_id")),
    calculated_source: str(fd.get("calculated_source")),
    form_elapsed_ms: str(fd.get("form_elapsed_ms")),
    form_token: str(fd.get("form_token")),
    // Honeypot values go to the server, which flags the lead "[Possible spam]"
    // instead of dropping it (browser autofill can fill hidden fields).
    x_reference: str(fd.get("x_reference")),
    botcheck: str(fd.get("botcheck")),
  };
}

/**
 * Reads the already-collected form fields and sends them to the edge function.
 * Call from a form's onSubmit WITHOUT preventDefault so the native Web3Forms
 * POST (and /thank-you redirect) still happens.
 */
export function forwardLeadToHcp(form: HTMLFormElement): void {
  try {
    const url = endpoint();
    const payload = buildPayload(form);
    if (!payload) return;

    // Remember this enquiry so the /thank-you page (which only the server can
    // redirect to) can report it to Google Ads as a real lead.
    markLeadAwaiting(payload.submission_id);

    // Persist first: if the beacon is blocked or dropped, the next page load
    // re-sends it.
    enqueue(payload.submission_id, payload);
  } catch {
    /* never block the customer's submission */
  }
}

/**
 * Convenience handler for React forms: <form onSubmit={handleLeadSubmit}>
 *
 * IMPORTANT: this handler never calls preventDefault. The browser's own form POST
 * to Web3Forms is the delivery path — it cannot be blocked by CORS, privacy
 * extensions or a slow script, and Web3Forms then redirects the visitor to our
 * own /thank-you page via the form's hidden `redirect` field.
 */
export function handleLeadSubmit(event: React.FormEvent<HTMLFormElement>): void {
  const form = event.currentTarget;
  if (form.dataset.sending === "true") { event.preventDefault(); return; }
  form.dataset.sending = "true";
  const restores: Array<() => void> = [];
  form.querySelectorAll<HTMLButtonElement>('button[type="submit"], button:not([type])').forEach(button => {
    const original = Array.from(button.childNodes);
    restores.push(() => {
      button.disabled = false;
      button.replaceChildren(...original);
      button.removeAttribute("aria-busy");
    });
    button.disabled = true;
    button.textContent = "Sending…";
    button.setAttribute("aria-busy", "true");
  });
  // If the visitor comes Back (back/forward cache) or the POST never leaves,
  // re-enable the form so they can send again.
  const reset = () => {
    delete form.dataset.sending;
    restores.forEach(r => r());
    window.removeEventListener("pageshow", onShow);
  };
  const onShow = (e: PageTransitionEvent) => { if (e.persisted) reset(); };
  window.addEventListener("pageshow", onShow);
  window.setTimeout(() => { if (document.visibilityState === "visible" && form.isConnected) reset(); }, 15000);

  // Nothing is blocked in the browser: every submission goes to the server,
  // which flags suspicious ones "[Possible spam]" (still delivered) and only
  // drops the exact blocked name — recording it for the daily digest.
  const elapsed = leadFormElapsed(form);
  stampHidden(form, "form_elapsed_ms", String(elapsed));

  // Remember the city the visitor entered so the thank-you page can personalize.
  try {
    const fd = new FormData(form);
    const city = str(fd.get("city"));
    if (typeof sessionStorage !== "undefined") {
      if (city) {
        sessionStorage.setItem("tron_lead_city", city);
      } else {
        sessionStorage.removeItem("tron_lead_city");
      }
    }
  } catch {
    /* never block the customer's submission */
  }

  forwardLeadToHcp(form);
}

/** Milliseconds since this page (and its form) loaded. */
export function formElapsedMs(): number {
  try {
    return Math.round(performance.now());
  } catch {
    return 999999;
  }
}

function stampHidden(form: HTMLFormElement, name: string, value: string): void {
  try {
    let input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  } catch {
    /* never block the customer's submission */
  }
}
